<template>
  <v-dialog
    content-class="add-new-item-popup"
    :value="internalValue"
    :width="width"
    @click:outside="onCloseClick"
    @keydown.esc="onCloseClick"
  >
    <v-card class="popup-wrapper" >
      <div class="content">
        <div class="add-title">
          {{ title }}
        </div>
        <div v-if='isForDomain' class='domain-add-warning-note'>
          <v-alert
            outlined
            type="info"
          >
            {{ $t('configuration.add.domain.warning') }}
          </v-alert>
        </div>
        <div class="add-item-form" :style="`width: ${width}`">
          <div class='error' v-if='showWarning'> {{ $t('add.new.item.warning') }} </div>
          <div class="item-inputs" v-for="(value, index) in items" :key="index">
            <div class="text-field-section">
              <text-field
                :ref="`url_${index}`"
                type="text"
                v-model="items[index].url"
                class="login"
                :rules="[...validationRules, existingDomainValidationRule]"
                :label="`${label} #${index+1}`"
                :placeholder="placeholder"
                @valueChanged='updateValidation'
              />
              <v-autocomplete
                class-name="organization-select"
                :placeholder="$t('select')"
                :ref="`org_${index}`"
                :items='organizationsItems'
                item-text='name'
                item-value='id'
                :rules="[v => !!v]"
                :append-icon="'expand_more'"
                :label='$t("add.domain.select.organization.label")'
                v-model='items[index].organization'
                @change='updateValidation'
                dense
                outlined
                autofocus
                return-object
              />
            </div>
            <div v-if='!isForDomain' class='choose-tld-row-section'>
              <Select
                class-name="domain-select"
                v-model="items[index].tld"
                :options="listOfDomains"
                :title="$t('configuration.add.url.associate.tld')"
                :placeholder="$t('select')"
                size="small"
              />
            </div>
            <div class='delete-row-section'>
              <custom-tooltip class='delete-row-section__tooltip' placement="right" popper-class="light-tooltip">
                <div slot='buttonContent'>
                  <span
                    class="delete-icon material-icons"
                    @click="deleteCurrentItem(index)"
                  >highlight_off</span>
                </div>
                <div slot="content">
                  <slot name="tooltip">{{$t('delete')}}</slot>
                </div>
              </custom-tooltip>
            </div>
          </div>
          <div class="add-new-item-section">
            <div class="add-new-item-header">
              <label  class="add-new-item-add-button"  @click="addItem">
                <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
              </label>
            </div>
          </div>
          <div class="scan-employee-section">
            <custom-checkbox
              v-if='isForDomain'
              :value='scanEmployeesFlag'
              color="cold-marine"
              size="s"
              :title="$t('scan.employees')"
              @input="scanEmployeesFlag = !scanEmployeesFlag"
            />
          </div>
        </div>
      </div>
      <div class="error-message" v-if='existTld'>
        <v-alert
          text
          dense
          color="#e0255e"
          icon="info"
          border="left"
          class='not-valid-message'
        >
          {{ $t('configuration.add.domain.exist') }}<br />
          <span style='text-transform: capitalize'>{{ $t('configuration.domain.label') }}: </span> {{existTldValue}}
        </v-alert>
      </div>
      <div class="buttons">
        <v-btn small depressed class="cancel-button" @click="onCloseClick"> {{ $t('confirm.cancel') }} </v-btn>
        <v-btn small depressed class="save-button btn-primary" :disabled="saveButtonDisabled" @click="onSaveClick">
          {{ addButtonTitle }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import Select from '@/components/common/Select.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import TextField from '../common/TextField.vue';

export default {
  name: 'AddNewItemPopup',
  components: {
    CustomCheckbox,
    TextField,
    Select,
    CustomTooltip,
  },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: () => '',
    },
    placeholder: {
      type: String,
      default: () => '',
    },
    addButtonTitle: {
      type: String,
      default: () => '',
    },
    validationRules: {
      type: Array,
      default: () => ([]),
    },
    isForDomain: {
      type: Boolean,
      default: false,
    },
    isForHost: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [{ url: '', tld: '', organization: { name: this.companyData?.name || '', id: this.companyData?.id || '' }}],
      scanEmployeesFlag: true,
      existTld: false,
      existTldValue: '',
    };
  },
  computed: {
    ...mapGetters(['domains', 'configurationDomains', 'organizations', 'companyData']),
    existingDomainValidationRule() {
      return (v => !!v && !this.listOfDomains.includes(v.trim().toLowerCase()));
    },
    organizationsItems() {
      return this.organizations.map(org => ({
        id: org.id,
        name: org.name,
      }));
    },
    width() {
      if (this.isForDomain) {
        return 500;
      }
      return 700;
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    showWarning() {
      return !this.isForHost &&
        !this.isForDomain &&
        !this.items.every(item => (!item.url || (item.tld && (item.url.endsWith(item.tld) || item.url === item.tld))));
    },
    saveButtonDisabled() {
      const rules = [...this.validationRules, this.existingDomainValidationRule];
      const allItemsValid = this.items.every(item => {
        const url = item.url?.trim().toLowerCase() || '';
        const urlValid = rules.every(rule => rule(url) === true);
        const orgValid = !!item.organization?.id;
        return urlValid && orgValid;
      });
      if (!this.isForDomain && !this.isForHost) {
        const allAssociated = this.items.every(item => item.tld && (item.url.endsWith(item.tld) || item.url === item.tld));
        return !allItemsValid || !allAssociated;
      }
      return !allItemsValid;
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    internalValue(value) {
      if (value) {
        this.init();
      }
    },
  },
  methods: {
    updateValidation() {
      const rules = [...this.validationRules, this.existingDomainValidationRule];
      let lastTLD = '';
      this.existTld = this.items.some(item => {
        const isIncludes = this.listOfDomains.includes(item.url.trim().toLowerCase());
        lastTLD = isIncludes ? item.url : '';
        return isIncludes;
      });
      this.existTldValue = this.existTld ? lastTLD : '';
      // trigger Vuetify field-level error messages by running rules on each ref
      for (let i = 0; i < this.items.length; i += 1) {
        if (this.$refs[`url_${i}`]?.[0]) {
          rules.forEach(rule => rule(this.$refs[`url_${i}`][0].value));
        }
      }
    },
    init() {
      this.items = [{ url: '', tld: '', organization: { name: this.companyData?.name || '', id: this.companyData?.id || '' } }];
    },
    onCloseClick() {
      this.init();
      this.internalValue = false;
    },
    onSaveClick() {
      const payload = {};
      let newItems = this.items;
      if (this.isForDomain) {
        newItems = [];
        this.items.forEach(item => {
          newItems.push(
            {
              url: item.url.trim().toLowerCase(),
              tld: item.url.trim().toLowerCase(),
              companyId: item.organization?.id || this.companyData?.id,
            },
          );
        });
        payload.items = newItems;
        payload.scanEmployees = this.scanEmployeesFlag;
        this.$emit('add', payload);
      } else {
        this.$emit('add', newItems);
      }
      this.onCloseClick();
    },
    addItem() {
      this.items.push(
        {
          url: '',
          tld: '',
          organization: {
            name: this.items[this.items.length - 1].organization?.name || '',
            id: this.items[this.items.length - 1].organization?.id || '',
          },
        },
      );
    },
    deleteCurrentItem(index) {
      if (this.items.length > 1) {
        this.items.splice(index, 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';
.add-item-form {
  .error {
    background: transparent;
    text-transform: capitalize;
    color: var(--severity-critical);
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    margin-bottom: $space-4;
  }
  .item-inputs {
    flex: 50%;
    display: flex;
    align-items: center;
    margin-top: $space-4;
    .text-field-section {
      display: flex;
      justify-content: space-between;
      flex:0 0 rem(390);
      .v-text-field {
        margin-right: $space-1-5;
      }
    }
    .delete-row-section{
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      margin-left: $space-1;
      &__tooltip {
        background-color: transparent;
      }
    }
    .domain-select {
      flex:1;
      max-width: 20%;
    }
    .delete-icon {
      color: var(--severity-critical);
      cursor: pointer;
      font-size: $font-size-md;
      margin-left: $space-1-5;
      font-weight: bolder;
    }

    .dummy-icon {
      width: $space-4;
      height: $space-4;
    }
  }
  .add-new-item-section {
    padding: $space-0;
    margin-bottom: $space-5;
    .add-new-item-header {
      display: flex;
      justify-content: space-between;

      .add-new-item-add-button {
        color: var(--brand-primary);
        text-transform: uppercase;
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $space-4;
        text-align: center;
        margin-right: $space-3;
        cursor: pointer;

        .plus {
          font-size: $font-size-md;
        }
      }
    }
  }
}

.add-new-item-popup {

  .popup-wrapper {
    max-width: rem(750);
    padding: $space-8 $space-10;
    .content {
      .add-title {
        text-transform: uppercase;
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $space-4;
        margin-bottom: $space-4;
      }
    }

    .buttons {
      text-align: right;
      padding-top: rem(30);

      .cancel-button {
        box-sizing: border-box;
        height: $space-8;
        width: rem(86);
        border: 1px solid var(--brand-primary) !important;
        color: var(--brand-primary) !important;
        border-radius: $radius-sm;
        background-color: var(--bg-primary) !important;
        box-shadow: $shadow-sm;
      }

      .save-button {
        height: $space-8;
        border-radius: $radius-sm;
        border: $border-width-thin solid var(--brand-primary);
        background-color: var(--brand-primary);
        box-shadow: $shadow-sm;
        color: var(--text-inverse);
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $space-4;
        text-align: center;
        margin-left: $space-2;
      }
    }

    .error-message {
      margin-bottom: rem(-20);
      .not-valid-message {
        font-size: $font-size-xs;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $space-4;
      }
    }
  }
}
</style>
<style lang="scss">
.v-dialog__content--active {
  .add-new-item-popup {
    overflow-y: auto !important;
  }
}

// Fix X button alignment with input box (Vuetify floating label adds ~20px above input)
.add-item-form .item-inputs {
  margin-bottom: 16px !important;

  .delete-row-section {
    margin-left: 4px !important;
    flex: 0 0 auto !important;
    padding-top: 0 !important;
    margin-top: 0 !important;
  }
}
</style>
